import React, { useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DateInput } from '../../../common/DateInput';
import { Input } from '../../../common/Input';
import { Select } from '../../../common/Select';
import { Textarea } from '../../../common/Textarea';
import { Field } from '../components/Field';
import { SectionHeading } from '../components/SectionHeading';
import { YesNoRadio } from '../components/YesNoRadio';
import {
  MAX_CURRENT_GA_DAYS,
  MAX_CURRENT_GA_TOTAL_DAYS,
  MAX_CURRENT_GA_WEEKS,
  PREGNANCY_RISK_OPTIONS,
  PREGNANCY_TYPE_OPTIONS,
  selectCls,
} from '../constants';
import type { CurrentPregnancyData } from '../types';
import {
  BIO_KEY_TO_TYPE,
  BIO_LIMITS,
  calculateGAFromBio,
  deriveTotalDaysFromBiometry,
  hasAnyBiometry,
  type BioKey,
} from '../utils/biometry';

interface GrossesseTabProps {
  pregnancy: CurrentPregnancyData;
  errors: Record<string, string>;
  onUpdate: <K extends keyof CurrentPregnancyData>(
    key: K,
    value: CurrentPregnancyData[K]
  ) => void;
  onPatch: (patch: Partial<CurrentPregnancyData>) => void;
  onValidationChange: <K extends keyof CurrentPregnancyData>(
    key: K,
    error: string
  ) => void;
}

const PREGNANCY_DAYS = 280; // Naegele's rule.

export const GrossesseTab: React.FC<GrossesseTabProps> = ({
  pregnancy,
  errors,
  onUpdate,
  onPatch,
  onValidationChange,
}) => {
  const { t } = useTranslation('Patient');
  const { t: tPM } = useTranslation('PatientManagement');
  const [gaWeeksInput, setGaWeeksInput] = React.useState(pregnancy.ga_weeks);
  const [gaDaysInput, setGaDaysInput] = React.useState(pregnancy.ga_days);

  const lastEditedRef = useRef<'lmp' | 'edd' | 'ga' | 'biometry' | null>(null);
  const lastEditedBioRef = useRef<BioKey | null>(null);
  const gaRangeMessage = t('dossier_current_ga_range_error', {
    maxWeeks: MAX_CURRENT_GA_WEEKS,
    maxDays: MAX_CURRENT_GA_DAYS,
    defaultValue:
      'Gestational age must stay between 0 and 45 weeks and 6 days.',
  });
  const gaDateRangeMessage = t('dossier_current_pregnancy_date_range_error', {
    maxWeeks: MAX_CURRENT_GA_WEEKS,
    maxDays: MAX_CURRENT_GA_DAYS,
    defaultValue:
      'These dates imply a current gestational age outside the allowed range.',
  });

  React.useEffect(() => {
    setGaWeeksInput(pregnancy.ga_weeks);
  }, [pregnancy.ga_weeks]);

  React.useEffect(() => {
    setGaDaysInput(pregnancy.ga_days);
  }, [pregnancy.ga_days]);

  const biometrics: { key: BioKey; label: string }[] = [
    { key: 'preg_crl', label: t('dossier_crl') },
    { key: 'preg_bpd', label: t('dossier_bpd') },
    { key: 'preg_hc', label: t('dossier_hc') },
    { key: 'preg_ac', label: t('dossier_ac') },
    { key: 'preg_fl', label: t('dossier_fl') },
  ];

  const biometryValues = useMemo(
    () => ({
      preg_crl: pregnancy.preg_crl,
      preg_bpd: pregnancy.preg_bpd,
      preg_hc: pregnancy.preg_hc,
      preg_ac: pregnancy.preg_ac,
      preg_fl: pregnancy.preg_fl,
    }),
    [
      pregnancy.preg_crl,
      pregnancy.preg_bpd,
      pregnancy.preg_hc,
      pregnancy.preg_ac,
      pregnancy.preg_fl,
    ]
  );

  const biometryHasAny = hasAnyBiometry(biometryValues);

  // ------ derived display info ----------------------------------------
  const eddPassed = useMemo(() => {
    if (!pregnancy.estimated_due_date) return false;
    return dayjs().isAfter(dayjs(pregnancy.estimated_due_date), 'day');
  }, [pregnancy.estimated_due_date]);

  const gaSummary = useMemo(() => {
    if (
      errors.last_menstrual_period ||
      errors.estimated_due_date ||
      errors.ga_weeks ||
      errors.ga_days
    ) {
      return null;
    }
    const w = parseInt(pregnancy.ga_weeks || '0', 10);
    const d = parseInt(pregnancy.ga_days || '0', 10);
    if (!pregnancy.last_menstrual_period && !pregnancy.estimated_due_date) {
      return null;
    }
    if (Number.isNaN(w) && Number.isNaN(d)) return null;
    if (!w && !d) return null;
    return `${w}+${d}`;
  }, [
    pregnancy.ga_weeks,
    pregnancy.ga_days,
    pregnancy.last_menstrual_period,
    pregnancy.estimated_due_date,
    errors.estimated_due_date,
    errors.ga_days,
    errors.ga_weeks,
    errors.last_menstrual_period,
  ]);

  useEffect(() => {
    if (lastEditedRef.current !== 'lmp') return;
    if (!pregnancy.last_menstrual_period) {
      onValidationChange('last_menstrual_period', '');
      return;
    }
    const lmp = dayjs(pregnancy.last_menstrual_period).startOf('day');
    if (!lmp.isValid()) return;
    const today = dayjs().startOf('day');
    const diffDays = today.diff(lmp, 'day');
    if (diffDays < 0 || diffDays > MAX_CURRENT_GA_TOTAL_DAYS) {
      onValidationChange('last_menstrual_period', gaDateRangeMessage);
      return;
    }
    onValidationChange('last_menstrual_period', '');
    const newEdd = lmp.add(PREGNANCY_DAYS, 'day').format('YYYY-MM-DD');
    const w = diffDays >= 0 ? Math.floor(diffDays / 7) : 0;
    const d = diffDays >= 0 ? diffDays % 7 : 0;
    if (
      newEdd !== pregnancy.estimated_due_date ||
      String(w) !== pregnancy.ga_weeks ||
      String(d) !== pregnancy.ga_days
    ) {
      onPatch({
        estimated_due_date: newEdd,
        ga_weeks: String(w),
        ga_days: String(d),
      });
    }
  }, [pregnancy.last_menstrual_period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lastEditedRef.current !== 'edd') return;
    if (!pregnancy.estimated_due_date) {
      onValidationChange('estimated_due_date', '');
      return;
    }
    const edd = dayjs(pregnancy.estimated_due_date).startOf('day');
    if (!edd.isValid()) return;
    const newLmp = edd.subtract(PREGNANCY_DAYS, 'day').format('YYYY-MM-DD');
    const today = dayjs().startOf('day');
    const diffDays = today.diff(dayjs(newLmp), 'day');
    if (diffDays < 0 || diffDays > MAX_CURRENT_GA_TOTAL_DAYS) {
      onValidationChange('estimated_due_date', gaDateRangeMessage);
      return;
    }
    onValidationChange('estimated_due_date', '');
    const w = diffDays >= 0 ? Math.floor(diffDays / 7) : 0;
    const d = diffDays >= 0 ? diffDays % 7 : 0;
    if (
      newLmp !== pregnancy.last_menstrual_period ||
      String(w) !== pregnancy.ga_weeks ||
      String(d) !== pregnancy.ga_days
    ) {
      onPatch({
        last_menstrual_period: newLmp,
        ga_weeks: String(w),
        ga_days: String(d),
      });
    }
  }, [pregnancy.estimated_due_date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lastEditedRef.current !== 'ga') return;
    if (errors.ga_weeks || errors.ga_days) return;
    const w = parseInt(pregnancy.ga_weeks || '0', 10) || 0;
    const d = parseInt(pregnancy.ga_days || '0', 10) || 0;
    const totalDays = w * 7 + d;
    if (totalDays <= 0) return;
    if (totalDays > MAX_CURRENT_GA_TOTAL_DAYS) return;
    const today = dayjs().startOf('day');
    const newLmp = today.subtract(totalDays, 'day').format('YYYY-MM-DD');
    const newEdd = today
      .subtract(totalDays, 'day')
      .add(PREGNANCY_DAYS, 'day')
      .format('YYYY-MM-DD');
    if (
      newLmp !== pregnancy.last_menstrual_period ||
      newEdd !== pregnancy.estimated_due_date
    ) {
      onPatch({
        last_menstrual_period: newLmp,
        estimated_due_date: newEdd,
      });
    }
  }, [pregnancy.ga_weeks, pregnancy.ga_days]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lastEditedRef.current !== 'biometry') return;
    if (!biometryHasAny) {
      // Whoever cleared the last biometry value also cleared the derivation
      // anchor, but we deliberately leave LMP/EDD/GA in place so the user
      // doesn't lose data they explicitly typed earlier.
      return;
    }
    const preferCrl = lastEditedBioRef.current === 'preg_crl';
    const totalDays = deriveTotalDaysFromBiometry(biometryValues, preferCrl);
    if (totalDays === null || totalDays <= 0) return;
    if (totalDays > MAX_CURRENT_GA_TOTAL_DAYS) return;

    const w = Math.floor(totalDays / 7);
    const d = Math.floor(totalDays % 7);
    const today = dayjs().startOf('day');
    const newLmp = today
      .subtract(Math.round(totalDays), 'day')
      .format('YYYY-MM-DD');
    const newEdd = today
      .subtract(Math.round(totalDays), 'day')
      .add(PREGNANCY_DAYS, 'day')
      .format('YYYY-MM-DD');

    const patch: Partial<CurrentPregnancyData> = {};
    if (String(w) !== pregnancy.ga_weeks) patch.ga_weeks = String(w);
    if (String(d) !== pregnancy.ga_days) patch.ga_days = String(d);
    if (newLmp !== pregnancy.last_menstrual_period) {
      patch.last_menstrual_period = newLmp;
    }
    if (newEdd !== pregnancy.estimated_due_date) {
      patch.estimated_due_date = newEdd;
    }
    if (Object.keys(patch).length > 0) onPatch(patch);
  }, [
    pregnancy.preg_crl,
    pregnancy.preg_bpd,
    pregnancy.preg_hc,
    pregnancy.preg_ac,
    pregnancy.preg_fl,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBiometryChange = (key: BioKey, raw: string) => {
    lastEditedRef.current = 'biometry';
    lastEditedBioRef.current = key;
    const next = raw === '' ? null : Number(raw);
    onUpdate(key, next as CurrentPregnancyData[typeof key]);
  };

  const handleGaInputChange = (field: 'ga_weeks' | 'ga_days', raw: string) => {
    lastEditedRef.current = 'ga';

    if (field === 'ga_weeks') setGaWeeksInput(raw);
    else setGaDaysInput(raw);

    if (raw === '') {
      onValidationChange(field, '');
      onUpdate(field, '');
      return;
    }

    if (!/^\d+$/.test(raw)) {
      onValidationChange(field, gaRangeMessage);
      return;
    }

    const parsed = Number(raw);
    const max =
      field === 'ga_weeks' ? MAX_CURRENT_GA_WEEKS : MAX_CURRENT_GA_DAYS;

    if (!Number.isFinite(parsed) || parsed < 0 || parsed > max) {
      onValidationChange(field, gaRangeMessage);
      return;
    }

    onValidationChange(field, '');
    onUpdate(field, raw);
  };

  const handleRiskClassificationChange = (value: boolean | null) => {
    if (value === true) {
      onUpdate('grossesse_classee_a_risque', true);
      return;
    }
    onPatch({
      grossesse_classee_a_risque: value,
      risque_anemie: false,
      risque_hta: false,
      risque_diabete: false,
      risque_cardiopathie: false,
      risque_metrorragie: false,
      risque_infection: false,
      risque_preeclampsie: false,
      risque_eclampsie: false,
      risque_autre_details: '',
    });
  };

  return (
    <div className="space-y-6">
      <SectionHeading
        icon="🤰"
        title={t('dossier_current_pregnancy_title')}
        description={t('dossier_current_pregnancy_helper')}
      />

      <div className="space-y-4">
        <Field
          id="grossesse_type"
          label={t('dossier_pregnancy_type')}
          error={errors.grossesse_type}
        >
          <Select
            id="grossesse_type"
            className={selectCls}
            value={pregnancy.grossesse_type}
            placeholder={t('dossier_pregnancy_type_select')}
            onChange={(e) => onUpdate('grossesse_type', e.target.value)}
            options={PREGNANCY_TYPE_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* LMP + estimated checkbox */}
          <Field
            id="last_menstrual_period"
            label={t('dossier_lmp')}
            error={errors.last_menstrual_period}
          >
            <DateInput
              id="last_menstrual_period"
              name="last_menstrual_period"
              value={pregnancy.last_menstrual_period}
              onChange={(e) => {
                lastEditedRef.current = 'lmp';
                onValidationChange('last_menstrual_period', '');
                onUpdate('last_menstrual_period', e.target.value);
              }}
              max={dayjs().format('YYYY-MM-DD')}
            />
            <label className="mt-1.5 flex cursor-pointer select-none items-center gap-2 text-xs text-title-page-color-300 dark:text-gray-300">
              <input
                type="checkbox"
                checked={pregnancy.lmp_is_estimated}
                onChange={(e) => onUpdate('lmp_is_estimated', e.target.checked)}
                className="h-3.5 w-3.5 accent-blue-500"
              />
              {t('dossier_lmp_estimated')}
            </label>
          </Field>

          {/* EDD + estimated checkbox */}
          <Field
            id="estimated_due_date"
            label={t('dossier_edd')}
            error={errors.estimated_due_date}
          >
            <DateInput
              id="estimated_due_date"
              name="estimated_due_date"
              value={pregnancy.estimated_due_date}
              onChange={(e) => {
                lastEditedRef.current = 'edd';
                onValidationChange('estimated_due_date', '');
                onUpdate('estimated_due_date', e.target.value);
              }}
            />
            <label className="mt-1.5 flex cursor-pointer select-none items-center gap-2 text-xs text-title-page-color-300 dark:text-gray-300">
              <input
                type="checkbox"
                checked={pregnancy.edd_is_estimated}
                onChange={(e) => onUpdate('edd_is_estimated', e.target.checked)}
                className="h-3.5 w-3.5 accent-blue-500"
              />
              {t('dossier_edd_estimated')}
            </label>
          </Field>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-sm font-medium leading-none text-title-page-color-300 dark:text-gray-300">
              {t('dossier_gestational_age')}
            </p>
            {gaSummary && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-blue-500/15 dark:text-blue-200">
                {gaSummary} {t('dossier_weeks_abbr')}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="relative">
              <Input
                id="gestational_age_weeks"
                type="number"
                min={0}
                max={MAX_CURRENT_GA_WEEKS}
                placeholder={t('dossier_weeks')}
                className="pr-12"
                aria-invalid={Boolean(errors.ga_weeks)}
                value={gaWeeksInput}
                onChange={(e) => handleGaInputChange('ga_weeks', e.target.value)}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-title-page-color-300/70">
                {t('dossier_weeks_abbr')}
              </span>
            </div>
            <div className="relative">
              <Input
                id="gestational_age_days"
                type="number"
                min={0}
                max={MAX_CURRENT_GA_DAYS}
                placeholder={t('dossier_days')}
                className="pr-12"
                aria-invalid={Boolean(errors.ga_days)}
                value={gaDaysInput}
                onChange={(e) => handleGaInputChange('ga_days', e.target.value)}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-title-page-color-300/70">
                {t('dossier_days_abbr')}
              </span>
            </div>
          </div>
          {(errors.ga_weeks || errors.ga_days) && (
            <p className="mt-1 text-xs font-medium text-red-500">
              {errors.ga_weeks || errors.ga_days}
            </p>
          )}
          {eddPassed && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
              <Info size={13} />
              {t('dossier_edd_passed_warning')}
            </div>
          )}
          {(pregnancy.lmp_is_estimated || pregnancy.edd_is_estimated) && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300">
              <Info size={13} />
              {t('dossier_estimated_dates_note')}
            </div>
          )}
        </div>

        <Field
          id="grossesse_desiree"
          label={t('dossier_pregnancy_desired')}
          error={errors.grossesse_desiree}
        >
          <YesNoRadio
            name="grossesse_desiree"
            value={pregnancy.grossesse_desiree}
            onChange={(v) => onUpdate('grossesse_desiree', v)}
          />
        </Field>

        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-gray-700 dark:bg-gray-800/30">
          <Field
            id="grossesse_classee_a_risque"
            label={t('dossier_pregnancy_high_risk')}
            error={errors.grossesse_classee_a_risque}
            hint={t('dossier_pregnancy_high_risk_helper')}
          >
            <YesNoRadio
              name="grossesse_classee_a_risque"
              value={pregnancy.grossesse_classee_a_risque}
              onChange={handleRiskClassificationChange}
              includeUnknownOption
              unknownLabel={tPM('timeline.currentPregnancy.notDocumented')}
            />
          </Field>

          {pregnancy.grossesse_classee_a_risque === true && (
            <div className="mt-4 space-y-4">
              <p className="text-sm font-medium text-title-page-color-300 dark:text-gray-300">
                {t('dossier_pregnancy_risk_types')}
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {PREGNANCY_RISK_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer select-none items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-title-page-color-300 transition-colors hover:border-blue-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-600"
                  >
                    <input
                      type="checkbox"
                      checked={pregnancy[option.value]}
                      onChange={(event) =>
                        onUpdate(option.value, event.target.checked)
                      }
                      className="h-4 w-4 accent-blue-500"
                    />
                    {t(option.labelKey)}
                  </label>
                ))}
              </div>
              <Field
                id="risque_autre_details"
                label={t('dossier_pregnancy_risk_other')}
                error={errors.risque_autre_details}
              >
                <Textarea
                  id="risque_autre_details"
                  value={pregnancy.risque_autre_details}
                  placeholder={t('dossier_pregnancy_risk_other_placeholder')}
                  onChange={(event) =>
                    onUpdate('risque_autre_details', event.target.value)
                  }
                />
              </Field>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/40">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-title-page-color-100 dark:text-gray-200">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-base dark:bg-blue-900/30">
              📏
            </span>
            {t('dossier_biometrics')}
          </div>
          <span className="text-[11px] text-title-page-color-300 dark:text-gray-400">
            {t('dossier_biometrics_helper')}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {biometrics.map(({ key, label }) => {
            const bioType = BIO_KEY_TO_TYPE[key];
            const limit = BIO_LIMITS[bioType];
            const value = pregnancy[key] as number | null;
            const ga = calculateGAFromBio(bioType, value);
            const isOutOfRange = ga?.error === 'invalid';
            return (
              <Field
                key={key}
                id={`preg-${key}`}
                label={label}
                error={
                  errors[key] ||
                  (isOutOfRange ? t('dossier_biometry_out_of_range') : undefined)
                }
              >
                <div className="relative">
                  <Input
                    id={`preg-${key}`}
                    type="number"
                    step="0.01"
                    placeholder={`${limit.min}–${limit.max}`}
                    className="pr-12"
                    value={value ?? ''}
                    onChange={(e) => handleBiometryChange(key, e.target.value)}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium uppercase tracking-wide text-title-page-color-300/70">
                    {limit.unit}
                  </span>
                </div>
                {ga && !ga.error ? (
                  <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-500/15 dark:text-blue-200">
                    {ga.weeks}+{ga.days} {t('dossier_weeks_abbr')}
                  </span>
                ) : null}
              </Field>
            );
          })}
        </div>
        {biometryHasAny && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300">
            <Info size={13} />
            {t('dossier_biometry_derivation_note')}
          </div>
        )}
      </div>
    </div>
  );
};
